import { cn } from '@/lib/utils';
import { ChevronDown, Lightbulb } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { RISK_DEFAULTS, RISK_STYLES } from '../lib/risk-styles';
import type { ClauseFinding, EditableField } from '../types/contract-checker.types';
import { EditableBox } from './editable-box';

interface ClauseCardProps {
  finding: ClauseFinding;
  /** Whether this finding is selected for the download (shared across tabs). */
  isSelected: boolean;
  onToggleSelect: () => void;
  /** True when this finding matched the user's query (shows a badge). */
  isMatched?: boolean;
  /** Whether the per-clause Details panel is expanded. */
  isOpen: boolean;
  onToggle: () => void;
  /** Currently-edited field on THIS finding, or null if none. */
  editingField: EditableField | null;
  /** Live draft text — only meaningful when `editingField` is non-null. */
  draft: string;
  onStartEdit: (field: EditableField, currentValue: string) => void;
  onDraftChange: (next: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

/**
 * One row in the Final Review checklist.
 *
 *   ┌─ risk left-border (4px) ─────────────────────────────────┐
 *   │ ☑ [HIGH RISK]                              Details ▾    │
 *   │   Indemnification Obligations                            │
 *   │   ┌ Original ───────┐ ┌ Proposed Change ✎ ──┐            │
 *   │   │ "...verbatim..." │ │ Capped at 12 months  │            │
 *   │   └─────────────────┘ └─────────────────────┘            │
 *   │   ▼ when open                                            │
 *   │   ┌ Full Counter-Clause ✎ ───────────────────┐            │
 *   │   │ Notwithstanding anything to the contrary…│            │
 *   │   └────────────────────────────────────────┘            │
 *   │   💡 Notes: industry standard for SaaS providers.        │
 *   └──────────────────────────────────────────────────────────┘
 *
 * Original is intentionally read-only (no `<EditableBox>`) since it
 * mirrors the uploaded contract — editing it would falsify the
 * comparison.
 */
export function ClauseCard({
  finding,
  isSelected,
  onToggleSelect,
  isMatched = false,
  isOpen,
  onToggle,
  editingField,
  draft,
  onStartEdit,
  onDraftChange,
  onSave,
  onCancel,
}: ClauseCardProps) {
  const { t } = useTranslation();
  const style = RISK_STYLES[finding.risk];

  // The Details panel only carries `fullCounterClause` + `notes`. When
  // neither is present there's nothing to expand — hide the toggle so
  // the user isn't lured into a dead click.
  const hasDetails = Boolean(finding.fullCounterClause) || Boolean(finding.notes);

  const editLabel = t('ORGCONTRACTCHECKER_RESULT_EDIT', { defaultValue: 'Edit' });
  const saveLabel = t('ORGCONTRACTCHECKER_RESULT_SAVE', { defaultValue: 'Save' });
  const cancelLabel = t('ORGCONTRACTCHECKER_RESULT_CANCEL', { defaultValue: 'Cancel' });

  return (
    <article
      className={cn(
        'bg-card border border-border border-l-4 rounded-r-lg p-4 sm:p-5 hover:shadow-md transition-shadow',
        style.border
      )}
    >
      <div className="flex items-start gap-3 sm:gap-4">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="mt-1 w-5 h-5 rounded-sm border-2 border-selise-blue accent-selise-blue-500 shrink-0 cursor-pointer"
          aria-label={t('ORGCONTRACTCHECKER_RESULT_INCLUDE', {
            defaultValue: 'Include in download',
          })}
        />
        <div className="flex-1 min-w-0">
          <div className="flex justify-between items-center mb-2 gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <span
                className={cn(
                  'text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded',
                  style.chip
                )}
              >
                {t(style.chipLabelKey, { defaultValue: RISK_DEFAULTS[finding.risk] })}
              </span>
              {isMatched && (
                <span className="text-[10px] sm:text-[11px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded bg-selise-blue-500/10 text-selise-blue-500">
                  {t('ORGCONTRACTCHECKER_RESULT_MATCHED', { defaultValue: 'Matches your query' })}
                </span>
              )}
            </div>
            {hasDetails && (
              <button
                type="button"
                onClick={onToggle}
                className="text-selise-blue-500 text-sm font-semibold flex items-center gap-1 hover:underline shrink-0"
                aria-expanded={isOpen}
                aria-controls={`clause-detail-${finding.id}`}
              >
                {t('ORGCONTRACTCHECKER_RESULT_DETAILS', { defaultValue: 'Details' })}
                <ChevronDown
                  className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')}
                />
              </button>
            )}
          </div>

          <h3 className="text-base sm:text-lg font-semibold text-[hsl(var(--fg))] mb-3 font-heading">
            {finding.title}
          </h3>

          <div className="grid md:grid-cols-2 gap-3 text-sm">
            {/* Original — read-only; this is the source contract text. */}
            <div className="p-3 bg-muted/60 border border-border rounded">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-globe-grey mb-1">
                {t('ORGCONTRACTCHECKER_RESULT_ORIGINAL', { defaultValue: 'Original' })}
              </p>
              <p className="text-globe-grey italic wrap-break-word">{finding.original}</p>
            </div>

            <EditableBox
              variant="proposed"
              label={t('ORGCONTRACTCHECKER_RESULT_PROPOSED', { defaultValue: 'Proposed Change' })}
              value={finding.proposedChange}
              isEditing={editingField === 'proposedChange'}
              draft={draft}
              onStartEdit={() => onStartEdit('proposedChange', finding.proposedChange)}
              onDraftChange={onDraftChange}
              onSave={onSave}
              onCancel={onCancel}
              editLabel={editLabel}
              saveLabel={saveLabel}
              cancelLabel={cancelLabel}
            />
          </div>

          {isOpen && (finding.fullCounterClause || finding.notes) && (
            <div
              id={`clause-detail-${finding.id}`}
              className="bg-muted/40 rounded-lg p-4 mt-3 border border-border"
            >
              {finding.fullCounterClause !== undefined && (
                <div className="mb-4">
                  <EditableBox
                    variant="counter"
                    label={t('ORGCONTRACTCHECKER_RESULT_FULLCLAUSE', {
                      defaultValue: 'Full Counter-Clause',
                    })}
                    value={finding.fullCounterClause ?? ''}
                    isEditing={editingField === 'fullCounterClause'}
                    draft={draft}
                    onStartEdit={() =>
                      onStartEdit('fullCounterClause', finding.fullCounterClause ?? '')
                    }
                    onDraftChange={onDraftChange}
                    onSave={onSave}
                    onCancel={onCancel}
                    editLabel={editLabel}
                    saveLabel={saveLabel}
                    cancelLabel={cancelLabel}
                  />
                </div>
              )}
              {finding.notes && (
                <div className="flex gap-2 items-start text-globe-grey">
                  <Lightbulb className="w-4 h-4 text-selise-blue shrink-0 mt-0.5" />
                  <p className="text-xs italic">
                    {t('ORGCONTRACTCHECKER_RESULT_NOTES_PREFIX', { defaultValue: 'Notes:' })}{' '}
                    {finding.notes}
                  </p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
}

export default ClauseCard;
